import classNames from "classnames"
import { cloneElement, useEffect, useState } from "react"

import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from "react-router-dom"

import {
  Button,
  FileDropZone,
  Icon,
  Toolbar,
  useCurrentView,
  ViewActions,
  ViewFilters,
  ViewSettings,
} from "web/components"

import useDebouncedValue from "web/hooks/behaviour/useDebouncedValue"
import { useCurrentUser, useListType, useObjectList } from "web/hooks/data"
import { isNonEmptyArray } from "web/services/helpers"
import { pluralize, titleize } from "web/services/inflector"

import routes from "web/routes/constants"
import { LocalizationContext } from "web/contexts"

import CardView from "./CardView"
import MapView from "./MapView"
import TableView from "./TableView/TableView"
import CompareView from "./CompareView/CompareView"
import OverviewView from "web/components/ObjectList/OverviewView"

import ObjectListViewModeSelector from "./ObjectListViewModeSelector"
import SelectedItemActions from "./SelectedItemActions"
import BreadcrumbNav from "./BreadcrumbNav"
import NoData from "./NoData"
import Loading from "./Loading"
import MarketViewableSelector from "./MarketViewableSelector"

function deriveViewModes(availableViews) {
  const supportsSplit =
    availableViews.includes("table") && availableViews.includes("map")

  return supportsSplit ? [...availableViews, "split"] : availableViews
}

function getBreadCrumbs({ subjectType, topic, viewGroup, view }) {
  const section = pluralize(subjectType)

  const viewGroupName = !!topic ? pluralize(topic.label) : viewGroup.name

  const crumbs = [
    { name: titleize(section) },
    {
      href: routes.view(section, viewGroup.slug, null, topic?.name),
      name: viewGroupName,
      current: view.isViewGroup,
    },
  ]

  if (!view.isViewGroup) {
    crumbs.push({
      href: routes.view(section, viewGroup.slug, view.id, topic?.name),
      name: view.name,
      current: true,
    })
  }

  return crumbs
}

function ObjectList({
  subjectType,
  subjectId,
  parentSubjectType,
  parentSubjectId,
  fetchURL: fetchURLFromProps,
  exportPath,
  listType: listTypeFromProps,
  topic,
  availableViews = [],
  defaultView: defaultViewFromProps = "table",
  customListActions = null,
  fileDropZoneActive,
  selectedItemActions = [],
  enableScheduledExport = false,
  enableExport = true,
  enableEdit = true,
  isFavouriteEnabled = true,
  onAdd,
  onItemClick,
  onFilesDropped,
  rootUrl,
  canRedirectToRootUrl = true,
  breadcrumbs = null,
  selectedIds: initialSelectedIds = [],
}) {
  const {
    view,
    viewGroup,
    page,
    setPage,
    viewLocalizationContextValue,
    handleSortChanged,
    handleRowsPerPageChanged,
    handleFrozenFieldChanged,
    handleGroupChanged,
    handleUpdateViewUnits,
    handleViewShare,
    handlePipelineStatistics,
    handleViewUpdate,
    handleViewSaveAs,
    handleViewDelete,
    handleViewReset,
    handleViewRename,
    handleAddCondition,
    handleChangeCondition,
    handleDeleteCondition,
    handleAddFieldToConditions,
    handleAddDistanceToConditions,
    handleChangeDistanceCondition,
    overview,
  } = useCurrentView()
  const currentUser = useCurrentUser()

  const [selectedIds, updateSelectedIds] = useState(() => {
    if (initialSelectedIds.length) {
      sessionStorage.setItem(
        "selectedSubjectIdsToCompare",
        JSON.stringify(initialSelectedIds)
      )
    }
    return initialSelectedIds
  })
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)

  const isMobile = window.innerWidth < 650
  const cardsAvailable = availableViews.includes("cards")
  const defaultView =
    isMobile && cardsAvailable ? "cards" : defaultViewFromProps

  const viewModes = deriveViewModes(availableViews)

  const hasParent = parentSubjectType !== undefined
  const hasSubject = subjectId !== undefined
  const hasTopic = topic !== undefined
  const isNestedTopicList = hasSubject && hasTopic
  const isNestedList = hasParent || isNestedTopicList

  const topicId = hasTopic ? topic.id : undefined

  const { viewMode } = useParams()

  const currentViewMode = viewMode || defaultView
  const isSplitView = currentViewMode === "split"
  const showBreadcrumbNav = !isNestedList || breadcrumbs
  const canShowFavouriteColumn =
    isFavouriteEnabled && !hasTopic && subjectType !== "task"

  function setSelectedIds(ids) {
    updateSelectedIds(ids)
    sessionStorage.setItem("selectedSubjectIdsToCompare", JSON.stringify(ids))
  }

  function handleSelectAllChanged(selectAll) {
    if (selectAll) {
      setSelectedIds(data.map((el) => el.id))
    } else {
      setSelectedIds([])
    }
  }

  function handleSelectionChanged(value) {
    if (value.selected) {
      setSelectedIds([...selectedIds, value.id])
    } else {
      setSelectedIds(selectedIds.filter((el) => el !== value.id))
    }
  }

  const viewModeRouteMatch = (viewMode) =>
    viewMode === defaultView ? rootUrl : `${rootUrl}/${viewMode}`

  const tableOrMapPaths = ["table", "map", "split"]
    .filter((mode) => viewModes.includes(mode))
    .map(viewModeRouteMatch)

  const isTableOrMapPath = tableOrMapPaths.length > 0

  const { listType, listTypeFriendlyName } = useListType({
    listType: listTypeFromProps,
    parentSubjectType,
    subjectType,
    topic,
  })

  const fetchResult = useObjectList({
    subjectType,
    subjectId,
    parentSubjectType,
    parentSubjectId,
    searchQuery: debouncedSearchQuery,
    topic: topicId,
    view,
    url: fetchURLFromProps,
    page: page,
    enabled: currentViewMode !== "map",
    includeLocationOrdinals: isSplitView,
  })

  const {
    detail: data,
    group_totals: groupTotals,
    grand_totals: grandTotals,
    sticky_group_totals: stickyGroupTotals,
    sticky_grand_totals: stickyGrandTotals,
    group_collection: groupCollection,
    page: pageMeta,
  } = fetchResult

  const selectedItems = []
  for (const datum of data) {
    if (selectedIds.includes(datum.id)) {
      datum.selected = true
      selectedItems.push(datum)
    } else {
      datum.selected = false
    }
  }

  const viewProps = {
    subjectType,
    subjectId,
    topic,
    data,
    groupCollection,
    fields: view.fields,
    viewType: view.type,
    viewId: view.id,
    onSelectionChanged: handleSelectionChanged,
    onSelectAllChanged: handleSelectAllChanged,
    onItemClick: onItemClick,
    selectedIds,
  }

  function handlePageChanged(page) {
    setPage(page)
  }

  function handleTableRowClick({ data: itemData }) {
    onItemClick(itemData, data)
  }

  const hasData = isNonEmptyArray(data)
  const canAdd = typeof onAdd === "function"

  function refreshObjectList() {
    data.refresh()
  }

  useEffect(() => {
    document.addEventListener(
      "event-dispatcher:refresh-object-list",
      refreshObjectList
    )
    return () =>
      document.removeEventListener(
        "event-dispatcher:refresh-object-list",
        refreshObjectList
      )
  })

  return (
    <LocalizationContext.Provider value={viewLocalizationContextValue}>
      <Toolbar>
        <Toolbar.Heading>
          {showBreadcrumbNav && (
            <BreadcrumbNav
              crumbs={
                breadcrumbs ||
                getBreadCrumbs({ subjectType, topic, viewGroup, view })
              }
            />
          )}
          {parentSubjectType === "market" &&
            view.selectable_viewables.length > 1 && (
              <MarketViewableSelector
                currentView={view}
                parentSubjectSlug={viewGroup.parent_subject_slug}
                subjectType={subjectType}
                topicName={viewGroup.topic_name}
                viewMode={viewMode}
              />
            )}
          <ViewActions
            view={view}
            onViewSaveAs={handleViewSaveAs}
            onViewReset={handleViewReset}
            onViewUpdate={handleViewUpdate}
          />
        </Toolbar.Heading>
        <Toolbar.Actions>
          <div className="flex items-center space-x-2">
            <Loading isLoading={data.isValidating} />
            {selectedItems.length > 0 && selectedItemActions.length > 0 && (
              <SelectedItemActions
                actions={selectedItemActions}
                data={data}
                selected={selectedItems}
              />
            )}
            {customListActions && cloneElement(customListActions, { data })}
            {canAdd && (
              <Button
                className="gap-1"
                onClick={() => onAdd(data)}
                testId={`button-add-${subjectType}`}
              >
                <Icon name="plus" />
                <span>Add</span>
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {viewModes.length > 1 && (
              <ObjectListViewModeSelector
                availableViews={viewModes}
                currentViewMode={currentViewMode}
                defaultView={defaultView}
                rootUrl={rootUrl}
              />
            )}
            <ViewSettings
              view={view}
              onDelete={handleViewDelete}
              onRename={handleViewRename}
              onShare={handleViewShare}
              onUpdateUnits={handleUpdateViewUnits}
              onPipelineStatistics={handlePipelineStatistics}
            />
          </div>
        </Toolbar.Actions>
      </Toolbar>
      <div
        id="object-list-body"
        className="flex min-h-0 grow flex-col overflow-hidden"
      >
        <ViewFilters
          subjectType={subjectType}
          listType={listType}
          topic={topic}
          availableFields={view.available_fields}
          filterConditions={view.conditions}
          groupConditions={view.group_conditions}
          stickyConditions={view.sticky_conditions || []}
          handleAddCondition={handleAddCondition}
          handleChangeCondition={handleChangeCondition}
          handleDeleteCondition={handleDeleteCondition}
          handleAddFieldToConditions={handleAddFieldToConditions}
          handleAddDistanceToConditions={handleAddDistanceToConditions}
          handleChangeDistanceCondition={handleChangeDistanceCondition}
          containerClasses="my-2 px-3 md:my-3 md:px-5"
        />
        <Switch>
          {isTableOrMapPath && (
            <Route exact path={tableOrMapPaths}>
              <div className="flex h-full min-h-0">
                {currentViewMode !== "table" && (
                  <Pane
                    widthClass={classNames({
                      "w-full": !isSplitView,
                      "hidden sm:flex sm:basis-[45%]": isSplitView,
                    })}
                  >
                    <MapView
                      subjectType={subjectType}
                      subjectId={subjectId}
                      parentSubjectType={parentSubjectType}
                      parentSubjectId={parentSubjectId}
                      searchQuery={debouncedSearchQuery}
                      topic={topic}
                      view={view}
                      url={fetchURLFromProps}
                      selectedIds={selectedIds}
                      onSelectionChanged={handleSelectionChanged}
                      onItemClick={onItemClick}
                      isSplitView={isSplitView}
                      page={page}
                    />
                  </Pane>
                )}
                {currentViewMode !== "map" && (
                  <Pane
                    widthClass={classNames("w-full", {
                      "sm:w-auto sm:basis-[55%] sm:min-w-[400px]": isSplitView,
                    })}
                  >
                    <div className="flex min-h-0 min-w-0 grow flex-col pl-3 md:pl-5">
                      <TableView
                        page={page}
                        pageCount={pageMeta?.page_count}
                        recordCount={pageMeta?.record_count}
                        rowsPerPage={view.table.rows_per_page}
                        parentSubjectType={parentSubjectType}
                        parentSubjectId={parentSubjectId}
                        onRowClick={handleTableRowClick}
                        onSort={handleSortChanged}
                        onGroup={handleGroupChanged}
                        onRowsPerPageChanged={handleRowsPerPageChanged}
                        onFrozenFieldChanged={handleFrozenFieldChanged}
                        handlePageChanged={handlePageChanged}
                        exportPath={exportPath}
                        listType={listType}
                        view={view}
                        enableScheduledExport={
                          enableScheduledExport && currentUser.is_super_user
                        }
                        enableExport={enableExport}
                        enableEdit={enableEdit}
                        enableFavourites={canShowFavouriteColumn}
                        showRowNumbers={isSplitView}
                        groupTotals={groupTotals}
                        grandTotals={grandTotals}
                        stickyGroupTotals={stickyGroupTotals}
                        stickyGrandTotals={stickyGrandTotals}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        {...viewProps}
                        emptyState={
                          <NoData
                            handleAddItem={canAdd && (() => onAdd(data))}
                            listTypeFriendlyName={listTypeFriendlyName}
                          />
                        }
                      />
                    </div>
                  </Pane>
                )}
              </div>
            </Route>
          )}
          {availableViews.includes("cards") && (
            <Route path={viewModeRouteMatch("cards")} exact>
              {hasData ? (
                <CardView {...viewProps} />
              ) : (
                <div className="pl-3 md:pl-5">
                  <NoData
                    handleAddItem={canAdd && (() => onAdd(data))}
                    listTypeFriendlyName={listTypeFriendlyName}
                  />
                </div>
              )}
            </Route>
          )}
          {availableViews.includes("overview") && (
            <Route path={viewModeRouteMatch("overview")} exact>
              <OverviewView view={view} overview={overview} />
            </Route>
          )}
          <Route path={viewModeRouteMatch("compare")} exact>
            {hasData ? (
              <CompareView
                parentSubjectType={parentSubjectType}
                parentSubjectId={parentSubjectId}
                listType={listType}
                subjectId={subjectId}
                setSelectedIds={setSelectedIds}
                view={view}
                {...viewProps}
              />
            ) : (
              <div className="pl-3 md:pl-5">
                <NoData
                  handleAddItem={canAdd && (() => onAdd(data))}
                  listTypeFriendlyName={listTypeFriendlyName}
                />
              </div>
            )}
          </Route>
          {canRedirectToRootUrl && (
            <Route>
              <Redirect to={{ pathname: rootUrl }} />
            </Route>
          )}
        </Switch>
      </div>
      {fileDropZoneActive && (
        <FileDropZone
          fullPage={true}
          onFilesDropped={(files) => onFilesDropped(files, data)}
        />
      )}
    </LocalizationContext.Provider>
  )
}

function Pane({ widthClass, children }) {
  return (
    <div className={`flex min-w-0 overflow-hidden ${widthClass}`}>
      {children}
    </div>
  )
}

export default function ObjectListNavigationWrapper(props) {
  const match = useRouteMatch()
  const matchUrlHasValue = match.url !== "/"

  const rootUrl = matchUrlHasValue ? match.url : props.rootUrl

  return (
    <Switch>
      <Route path={`${rootUrl}/:viewMode?`}>
        <ObjectList {...props} rootUrl={rootUrl} />
      </Route>
      <Route>
        <Redirect to={{ pathname: rootUrl }} />
      </Route>
    </Switch>
  )
}
